import React, { useEffect, useState } from 'react';
import { injectIntl, IntlShape } from 'react-intl';
import { get, isEmpty } from 'lodash';
import {
  COMPTABLE_EXPERTISE_ID,
  JUDGEMENT_EXPERTISE_TYPES,
  MECHANICAL_EXPERTISE_ID,
  MEDICAL_EXPERTISE_ID,
  MODE_TYPES,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/constants';
import * as featureService from 'features/fflip-helpers';
import { FEATURES } from 'features/features';
import messages from 'containers/LawyerLegalProceedingsPage/messages';
import { translate } from 'containers/services/translate';
import { JudgementCivilContainer } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/styled-components';
import { EMPTY_EXPERTISE, EXPERTISES } from 'containers/data-structure';
import { Expertise } from 'containers/common/LegalProceedings/JudgementAdd/Forms/JudgementAddPrononcings/typings';
import PrimaryButton from 'components/Buttons/PrimaryButton';
import SecondaryButton from 'components/Buttons/SecondaryButton';
import { BUTTON_COLORS } from 'constants/design';
import { IExpertises } from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/typings';
import {
  getExpertisejudgementAddDocId,
  hasAccountingExpertise,
  shouldDisplayExpertiseType,
  formatExpertiseV2,
  formatExpertise,
  validateCivilJudgement,
} from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/utils';
import { getVictimFromSagisInfo } from 'containers/PreOpeningPage/utils';
import { getVehicleFromSagisInfo } from 'containers/PreOpeningPage/OpposingParty/utils';
import JudgementForNonCompensation from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/JudgementForNonCompensation';
import VictimExpertise from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/VictimExpertise';
import { Listings } from 'containers/AppContainer/typings';
import VehicleExpertise from 'containers/LawyerLegalProceedingsPage/JudgementOnMerits/VehicleExpertise';

export interface CivilJudgementProps {
  intl: IntlShape;
  judgementAddId: any;
  judgementAddDocExpertises: Expertise[];
  handleCivilJudgementChange: Function;
  dahirAmounts: { [key: string]: number };
  isAppeal?: boolean;
  isDisabled: any;
  setIsDisabled: Function;
  judgementAddAppeal?: any;
  handleCancelCivilJudgement: Function;
  sharedResponsability: String;
  sagisVictimInfo: any;
  sagisOpposingParties: any;
  listings: Listings;
  setJudgementOnMeritsRequest: Function;
  judgementOnMeritsRequest: any;
  judgement: any;
  setJudgement: Function;
  allJudgementDocuments: any;
}
const CivilJudgement: React.FC<CivilJudgementProps> = ({
  judgementAddId,
  judgementAddDocExpertises,
  handleCivilJudgementChange,
  isAppeal,
  judgementAddAppeal,
  ...props
}): JSX.Element => {
  const [expertises, setExpertises] = useState<IExpertises>(EMPTY_EXPERTISE);
  const [isInputChanged, setIsInputChanged] = useState<boolean>(true);
  useEffect(() => {
    props.setIsDisabled({ ...props.isDisabled, [judgementAddId]: false });
  }, [judgementAddId]);
  const medicalExpertisesIds = props.allJudgementDocuments
    .flatMap((item: any) => item.expertises)
    .filter((item: any) => String(item.expertiseId) === MEDICAL_EXPERTISE_ID)
    .flatMap((item: any) => get(item, 'victims', []))
    .map((v: any) => v.id);

  const mechanicalExpertiseIds = props.allJudgementDocuments
    .flatMap((item: any) => item.expertises)
    .filter((item: any) => String(item.expertiseId) === MECHANICAL_EXPERTISE_ID)
    .flatMap((item: any) => get(item, 'vehicules', []))
    .map((v: any) => v.id);

  const accountingExp = props.allJudgementDocuments
    .flatMap((item: any) => item.expertises)
    .filter((item: any) => String(item.expertiseId) === COMPTABLE_EXPERTISE_ID);

  const recentAccountingExpertises = accountingExp
    .flatMap((item: any) => get(item, 'victims', []))
    .sort(
      (a: any, b: any) =>
        new Date(get(a, 'createdAt')).getTime() -
        new Date(get(b, 'createdAt')).getTime(),
    );

  /* istanbul ignore next */
  const recentAccountingVehiculesExpertises = accountingExp
    .flatMap((item: any) => get(item, 'vehicules', []))
    .sort(
      (a: any, b: any) =>
        new Date(get(a, 'createdAt')).getTime() -
        new Date(get(b, 'createdAt')).getTime(),
    );
  /* istanbul ignore next */
  const setMedicalVictimExpertise = (
    victimId: string,
    value: string,
    victimKey: string,
    name: string,
  ) => {
    const victimValue = {
      victimId: Number(victimId),
      victimKey: victimKey,
      [name]: value === '' ? '' : Number(value),
      calculatedCompensationAmount: 0,
      expertiseJudgementAddDocumentId: getExpertisejudgementAddDocId(
        victimId,
        MEDICAL_EXPERTISE_ID,
        judgementAddDocExpertises,
        judgementAddAppeal,
      ),
    };
    const expertiseToUpdate = [
      ...expertises[EXPERTISES.MEDICAL_EXPERTISE],
    ] as any;
    const victimIndex = expertiseToUpdate
      .map((el: { victimId: string }) => el.victimId)
      .indexOf(Number(victimId));
    if (victimIndex !== -1) {
      expertiseToUpdate[victimIndex] = {
        ...expertiseToUpdate[victimIndex],
        ...victimValue,
      };
    } else {
      expertiseToUpdate.push(victimValue);
    }
    return expertiseToUpdate;
  };

  const setAccountingVictimExpertise = (
    victimId: string,
    value: string,
    victimKey: string,
    name: string,
  ) => {
    const currentExpertise =
      recentAccountingExpertises.find(
        (item: any) => String(item.id) === victimId,
      ) || {};

    const victimValue = {
      victimId: Number(victimId),
      victimKey: victimKey,
      [name]: value === '' ? '' : Number(value),
      expertiseJudgementAddDocumentId: !featureService.hasFeature(
        FEATURES.REASONS_FOR_NON_COMPENSATION,
      )
        ? getExpertisejudgementAddDocId(
            victimId,
            COMPTABLE_EXPERTISE_ID,
            judgementAddDocExpertises,
            {},
          )
        : get(currentExpertise, 'expertiseJudgementAddDocumentId'),
    };
    const expertiseToUpdate = [
      ...expertises[EXPERTISES.ACCOUNTING_EXPERTISE],
    ] as any;
    const victimIndex = expertiseToUpdate
      .map((el: { victimId: string }) => el.victimId)
      .indexOf(Number(victimId));
    const currentMedicalExpertise =
      get(medicalExpertises, 'victims', []).find(
        (item: any) => Number(item.id) === Number(victimId),
      ) || {};
    if (victimIndex !== -1) {
      expertiseToUpdate[victimIndex] = {
        ...expertiseToUpdate[victimIndex],
        ...(!isEmpty(currentMedicalExpertise) &&
        featureService.hasFeature(FEATURES.REASONS_FOR_NON_COMPENSATION)
          ? {
              calculatedRevenue:
                name === 'compensationAmount'
                  ? get(victimValue, 'compensationAmount')
                  : get(expertiseToUpdate[victimIndex], 'compensationAmount'),
            }
          : undefined),
        ...victimValue,
      };
    } else {
      expertiseToUpdate.push(victimValue);
    }
    return expertiseToUpdate;
  };
  const handleVictimChange = (
    victimId: number | undefined,
    expertiseType: string,
    victimKey: string,
  ) => (e: React.ChangeEvent<{ value: string; name: string }>) => {
    const accountingExpertise =
      recentAccountingExpertises.find((item: any) => item.id === victimId) ||
      {};
    setIsInputChanged(false);
    if (expertiseType === MEDICAL_EXPERTISE_ID) {
      const expertiseMedToUpdate = setMedicalVictimExpertise(
        String(victimId),
        e.target.value.replace(',', '.'),
        victimKey,
        e.target.name,
      );
      const expertiseAccToUpdate = setAccountingVictimExpertise(
        String(victimId),
        e.target.value.replace(',', '.'),
        victimKey,
        e.target.name,
      );
      setExpertises(
        Object.assign(
          {},
          { ...expertises },
          {
            [EXPERTISES.MEDICAL_EXPERTISE]: [...expertiseMedToUpdate],
            ...(featureService.hasFeature(
              FEATURES.REASONS_FOR_NON_COMPENSATION,
            ) && !isEmpty(accountingExpertise)
              ? {
                  [EXPERTISES.ACCOUNTING_EXPERTISE]: [...expertiseAccToUpdate],
                }
              : undefined),
          },
        ),
      );
    } else {
      const expertiseAccToUpdate = setAccountingVictimExpertise(
        String(victimId),
        e.target.value.replace(',', '.'),
        victimKey,
        e.target.name,
      );
      setExpertises(
        Object.assign(
          {},
          { ...expertises },
          {
            [EXPERTISES.ACCOUNTING_EXPERTISE]: [...expertiseAccToUpdate],
          },
        ),
      );
    }
  };
  /* istanbul ignore next */
  const handleVehiculeChange = (
    vehiculeId: number | undefined,
    expertiseType: string,
  ) => (e: React.ChangeEvent<{ value: string; name: string }>) => {
    setIsInputChanged(false);
    let expertiseToUpdate: any = [];
    let accountingExpertiseToUpdate: any = [];
    const currentVehiculeExpertise =
      recentAccountingVehiculesExpertises.find(
        (item: any) => item.id === vehiculeId,
      ) || {};

    if (expertiseType === MECHANICAL_EXPERTISE_ID) {
      const mechanicalExpertise = expertises[
        EXPERTISES.MECHANICAL_EXPERTISE
      ].find((exp: any) => exp.vehiculeId === vehiculeId);
      expertiseToUpdate.push(
        ...expertises[EXPERTISES.MECHANICAL_EXPERTISE].filter(
          (exp: any) => exp.vehiculeId !== vehiculeId,
        ),
        {
          ...mechanicalExpertise,
          [e.target.name]:
            e.target.value === ''
              ? ''
              : Number(e.target.value.replace(',', '.')),
          vehiculeId: Number(vehiculeId),
          expertiseJudgmentAddDocumentId: getExpertisejudgementAddDocId(
            vehiculeId,
            MECHANICAL_EXPERTISE_ID,
            judgementAddDocExpertises,
            {},
          ),
        },
      );

      const accountingExpertise =
        expertises[EXPERTISES.ACCOUNTING_EXPERTISE].find(
          (exp: any) => exp.vehiculeId === vehiculeId,
        ) || ({} as any);
      accountingExpertiseToUpdate.push(
        ...expertises[EXPERTISES.ACCOUNTING_EXPERTISE].filter(
          (exp: any) => exp.vehiculeId !== vehiculeId,
        ),
        {
          ...accountingExpertise,
          [e.target.name]:
            e.target.value === ''
              ? ''
              : Number(e.target.value.replace(',', '.')),
          evaluationAmount:
            e.target.name === 'damageAmount'
              ? e.target.value
              : get(mechanicalExpertise, 'damageAmount'),
          vehiculeId: Number(vehiculeId),
          expertiseJudgementAddDocumentId: !featureService.hasFeature(
            FEATURES.REASONS_FOR_NON_COMPENSATION,
          )
            ? getExpertisejudgementAddDocId(
                vehiculeId,
                COMPTABLE_EXPERTISE_ID,
                judgementAddDocExpertises,
                {},
              )
            : get(currentVehiculeExpertise, 'expertiseJudgementAddDocumentId'),
        },
      );
      setExpertises(
        Object.assign(
          {},
          { ...expertises },
          {
            [EXPERTISES.MECHANICAL_EXPERTISE]: [...expertiseToUpdate],
            ...(featureService.hasFeature(
              FEATURES.REASONS_FOR_NON_COMPENSATION,
            ) && !isEmpty(currentVehiculeExpertise)
              ? {
                  [EXPERTISES.ACCOUNTING_EXPERTISE]: [
                    ...accountingExpertiseToUpdate,
                  ],
                }
              : undefined),
          },
        ),
      );
    } else {
      expertiseToUpdate.push(
        ...expertises[EXPERTISES.ACCOUNTING_EXPERTISE].filter(
          (exp: any) => exp.vehiculeId !== vehiculeId,
        ),
        {
          ...expertises[EXPERTISES.ACCOUNTING_EXPERTISE].find(
            (exp: any) => exp.vehiculeId === vehiculeId,
          ),
          [e.target.name]:
            e.target.value === ''
              ? ''
              : Number(e.target.value.replace(',', '.')),
          vehiculeId: Number(vehiculeId),
          expertiseJudgementAddDocumentId: getExpertisejudgementAddDocId(
            vehiculeId,
            COMPTABLE_EXPERTISE_ID,
            judgementAddDocExpertises,
            {},
          ),
        },
      );
      setExpertises(
        Object.assign(
          {},
          { ...expertises },
          {
            [EXPERTISES.ACCOUNTING_EXPERTISE]: [...expertiseToUpdate],
          },
        ),
      );
    }
  };
  const setMedicalExpertiseFieldValue = (
    victimId: string,
    field:
      | 'calculatedCompensationAmount'
      | 'compensationAmount'
      | 'provisionalExecution',
  ) => {
    const found = [...expertises[EXPERTISES.MEDICAL_EXPERTISE]].find(
      el => Number(el.victimId) === Number(victimId),
    );
    if (found) {
      return found[field];
    }
    return '';
  };
  /* istanbul ignore next */
  const setAccountingExpertiseFieldValue = (
    type: string,
    id: string,
    field: 'evaluationAmount' | 'calculatedRevenue' | 'provisionalExecution',
  ) => {
    const found = [...expertises[EXPERTISES.ACCOUNTING_EXPERTISE]].find(
      (el: any) => Number(el[type]) === Number(id),
    );
    if (found) return found[field];
    return '';
  };
  /* istanbul ignore next */
  const setMechanicalExpertiseFieldValue = (
    vehiculeId: string,
    field: 'damageAmount' | 'provisionalExecution',
  ) => {
    const found = [...expertises[EXPERTISES.MECHANICAL_EXPERTISE]].find(
      el => Number(el.vehiculeId) === Number(vehiculeId),
    );
    if (found) {
      return found[field];
    }
    return '';
  };
  const validateInputs = () => {
    return featureService.hasFeature(FEATURES.REASONS_FOR_NON_COMPENSATION)
      ? validateCivilJudgement(
          judgementAddDocExpertises,
          props.judgement,
          recentAccountingExpertises,
          recentAccountingVehiculesExpertises,
          expertises,
          get(props.judgementOnMeritsRequest, 'civjvm', []),
          get(props.judgementOnMeritsRequest, 'civjVehicle', []),
          get(props.listings, 'civilJudgementVictimReason', []),
        )
      : true;
  };
  const handleValidate = () => {
    if (!validateInputs()) setIsInputChanged(false);
    else {
      handleCivilJudgementChange(
        featureService.hasFeature(FEATURES.REASONS_FOR_NON_COMPENSATION)
          ? formatExpertiseV2(
              judgementAddId,
              props.allJudgementDocuments,
              expertises,
              props.sharedResponsability,
            )
          : formatExpertise(
              judgementAddId,
              expertises,
              props.sharedResponsability,
            ),
      );
      props.setIsDisabled({ ...props.isDisabled, [judgementAddId]: true });
    }
  };
  const handleCancel = () => {
    props.handleCancelCivilJudgement &&
      props.handleCancelCivilJudgement(judgementAddId, [
        ...recentAccountingVehiculesExpertises,
        ...recentAccountingExpertises,
      ]);
    props.setIsDisabled({ ...props.isDisabled, [judgementAddId]: false });
  };
  const accountingExpertises = shouldDisplayExpertiseType(
    COMPTABLE_EXPERTISE_ID,
    judgementAddDocExpertises,
  );
  const medicalExpertises = shouldDisplayExpertiseType(
    MEDICAL_EXPERTISE_ID,
    judgementAddDocExpertises,
  );
  const mechanicalExpertises = shouldDisplayExpertiseType(
    MECHANICAL_EXPERTISE_ID,
    judgementAddDocExpertises,
  );
  return (
    <JudgementCivilContainer>
      {!isEmpty(mechanicalExpertises) && (
        <div className="expertise">
          <div className="expertise-title">
            {translate(messages.mechanicalExpertise, props)}
          </div>
          {get(mechanicalExpertises, 'vehicules', []).map(
            (vehicule: any, index: number) => (
              <div key={vehicule.id} className="victims">
                <div className="victim-item">
                  {/* istanbul ignore next */ featureService.hasFeature(
                    FEATURES.MIGRATION_CLAIM_INFO,
                  ) &&
                  !isEmpty(
                    getVehicleFromSagisInfo(
                      get(vehicule, 'partKey'),
                      props.sagisOpposingParties,
                    ),
                  )
                    ? get(
                        getVehicleFromSagisInfo(
                          get(vehicule, 'partKey'),
                          props.sagisOpposingParties,
                        ),
                        'registrationNumber',
                      )
                    : `${vehicule.registrationNumber}`}
                </div>

                <>
                  {featureService.hasFeature(
                    FEATURES.REASONS_FOR_NON_COMPENSATION,
                  ) ? (
                    <JudgementForNonCompensation
                      judgementAddId={judgementAddId}
                      judgementAddDocExpertises={judgementAddDocExpertises}
                      dahirAmounts={props.dahirAmounts}
                      isAppeal={isAppeal}
                      setExpertiseFieldValue={setMechanicalExpertiseFieldValue}
                      vehicle={vehicule}
                      index={index}
                      expertises={expertises}
                      isInputChanged={isInputChanged}
                      expertiseType={EXPERTISES.MECHANICAL_EXPERTISE}
                      judgementOnMeritsRequest={props.judgementOnMeritsRequest}
                      setJudgementOnMeritsRequest={
                        props.setJudgementOnMeritsRequest
                      }
                      listings={props.listings}
                      setExpertises={setExpertises}
                      setJudgement={props.setJudgement}
                      judgement={props.judgement}
                      expertiseJudgementAddDocumentId={
                        vehicule?.expertiseJudgementAddDocumentId
                      }
                      judgementForNonCompensation={
                        JUDGEMENT_EXPERTISE_TYPES.CIVIL
                      }
                      handleVehiculeChange={handleVehiculeChange}
                      modeType={MODE_TYPES.FORM}
                      withAccountingExpertise={hasAccountingExpertise(
                        recentAccountingVehiculesExpertises,
                        vehicule?.id,
                      )}
                      isDisabled={get(props.isDisabled, judgementAddId, false)}
                      allJudgementDocuments={props.allJudgementDocuments}
                      sharedResponsability={props.sharedResponsability}
                    />
                  ) : (
                    <VehicleExpertise
                      dahirAmounts={props.dahirAmounts}
                      isAppeal={isAppeal}
                      setExpertiseFieldValue={setMechanicalExpertiseFieldValue}
                      handleVehiculeChange={handleVehiculeChange}
                      vehicle={vehicule}
                      index={index}
                      expertiseType={EXPERTISES.MECHANICAL_EXPERTISE}
                      expertises={expertises}
                      isInputChanged={isInputChanged}
                      sagisOpposingParties={props.sagisOpposingParties}
                      isDisabled={get(props.isDisabled, judgementAddId, false)}
                    />
                  )}
                </>
              </div>
            ),
          )}
        </div>
      )}
      {medicalExpertises && (
        <div className="expertise">
          {
            <div className="expertise-title">
              {translate(messages.medicalExpertise, props)}
            </div>
          }
          {get(medicalExpertises, 'victims', [])
            .filter((el: any) => el.injuryPostKey !== '2')
            .map((victim: any, index: any) => (
              <div key={victim.id} className="victims">
                <div className="victim-item">
                  {featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO)
                    ? `${get(
                        getVictimFromSagisInfo(
                          get(victim, 'victimKey'),
                          props.sagisVictimInfo,
                        ),
                        'victimFirstName',
                        '',
                      )} ${get(
                        getVictimFromSagisInfo(
                          get(victim, 'victimKey'),
                          props.sagisVictimInfo,
                        ),
                        'victimLastName',
                        '',
                      )}`
                    : `${victim.firstName} ${victim.lastName}`}
                </div>
                <>
                  {featureService.hasFeature(
                    FEATURES.REASONS_FOR_NON_COMPENSATION,
                  ) ? (
                    <JudgementForNonCompensation
                      judgementAddId={judgementAddId}
                      judgementAddDocExpertises={judgementAddDocExpertises}
                      dahirAmounts={props.dahirAmounts}
                      isAppeal={isAppeal}
                      setExpertiseFieldValue={setMedicalExpertiseFieldValue}
                      handleVictimChange={handleVictimChange}
                      victim={victim}
                      index={index}
                      expertises={expertises}
                      isInputChanged={isInputChanged}
                      expertiseType={EXPERTISES.MEDICAL_EXPERTISE}
                      judgementOnMeritsRequest={props.judgementOnMeritsRequest}
                      setJudgementOnMeritsRequest={
                        props.setJudgementOnMeritsRequest
                      }
                      listings={props.listings}
                      setExpertises={setExpertises}
                      setJudgement={props.setJudgement}
                      judgement={props.judgement}
                      expertiseJudgementAddDocumentId={
                        victim?.expertiseJudgementAddDocumentId
                      }
                      judgementForNonCompensation={
                        JUDGEMENT_EXPERTISE_TYPES.CIVIL
                      }
                      modeType={MODE_TYPES.FORM}
                      withAccountingExpertise={hasAccountingExpertise(
                        recentAccountingExpertises,
                        victim?.id,
                      )}
                      isDisabled={get(props.isDisabled, judgementAddId, false)}
                      allJudgementDocuments={props.allJudgementDocuments}
                      sharedResponsability={props.sharedResponsability}
                    />
                  ) : (
                    <VictimExpertise
                      dahirAmounts={props.dahirAmounts}
                      isAppeal={isAppeal}
                      setExpertiseFieldValue={setMedicalExpertiseFieldValue}
                      handleVictimChange={handleVictimChange}
                      victim={victim}
                      index={index}
                      expertiseType={EXPERTISES.MEDICAL_EXPERTISE}
                      expertises={expertises}
                      isInputChanged={isInputChanged}
                      isDisabled={get(props.isDisabled, judgementAddId, false)}
                    />
                  )}
                </>
              </div>
            ))}
        </div>
      )}

      {!isEmpty(accountingExpertises) && (
        <div className="expertise">
          <div className="expertise-title">
            {translate(messages.accountingExpertise, props)}
          </div>
          {get(accountingExpertises, 'victims', [])
            .filter((el: any) => el.injuryPostKey !== '2')
            .filter((v: any) =>
              featureService.hasFeature(FEATURES.REASONS_FOR_NON_COMPENSATION)
                ? !medicalExpertisesIds.includes(v.id)
                : true,
            )
            .map((victim: any, index: any) => (
              <>
                <div key={victim.id} className="victims">
                  <div className="victim-item">
                    {featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO)
                      ? `${get(
                          getVictimFromSagisInfo(
                            get(victim, 'victimKey'),
                            props.sagisVictimInfo,
                          ),
                          'victimFirstName',
                          '',
                        )} ${get(
                          getVictimFromSagisInfo(
                            get(victim, 'victimKey'),
                            props.sagisVictimInfo,
                          ),
                          'victimLastName',
                          '',
                        )}`
                      : `${victim.firstName} ${victim.lastName}`}
                  </div>
                  <>
                    {featureService.hasFeature(
                      FEATURES.REASONS_FOR_NON_COMPENSATION,
                    ) ? (
                      <JudgementForNonCompensation
                        judgementAddId={judgementAddId}
                        judgementAddDocExpertises={judgementAddDocExpertises}
                        dahirAmounts={props.dahirAmounts}
                        isAppeal={isAppeal}
                        setExpertiseFieldValue={
                          setAccountingExpertiseFieldValue
                        }
                        handleVictimChange={handleVictimChange}
                        victim={victim}
                        index={index}
                        expertises={expertises}
                        isInputChanged={isInputChanged}
                        expertiseType={EXPERTISES.ACCOUNTING_EXPERTISE}
                        judgementOnMeritsRequest={
                          props.judgementOnMeritsRequest
                        }
                        setJudgementOnMeritsRequest={
                          props.setJudgementOnMeritsRequest
                        }
                        listings={props.listings}
                        setExpertises={setExpertises}
                        setJudgement={props.setJudgement}
                        judgement={props.judgement}
                        expertiseJudgementAddDocumentId={
                          victim?.expertiseJudgementAddDocumentId
                        }
                        judgementForNonCompensation={
                          JUDGEMENT_EXPERTISE_TYPES.CIVIL
                        }
                        modeType={MODE_TYPES.FORM}
                        withAccountingExpertise={hasAccountingExpertise(
                          recentAccountingExpertises,
                          victim?.id,
                        )}
                        isDisabled={get(
                          props.isDisabled,
                          judgementAddId,
                          false,
                        )}
                        allJudgementDocuments={props.allJudgementDocuments}
                        sharedResponsability={props.sharedResponsability}
                      />
                    ) : (
                      <VictimExpertise
                        dahirAmounts={props.dahirAmounts}
                        isAppeal={isAppeal}
                        setExpertiseFieldValue={
                          setAccountingExpertiseFieldValue
                        }
                        handleVictimChange={handleVictimChange}
                        victim={victim}
                        index={index}
                        expertiseType={EXPERTISES.ACCOUNTING_EXPERTISE}
                        expertises={expertises}
                        isInputChanged={isInputChanged}
                        isDisabled={get(
                          props.isDisabled,
                          judgementAddId,
                          false,
                        )}
                      />
                    )}
                  </>
                </div>
              </>
            ))}
          {get(accountingExpertises, 'vehicules', [])
            .filter((v: any) =>
              featureService.hasFeature(FEATURES.REASONS_FOR_NON_COMPENSATION)
                ? !mechanicalExpertiseIds.includes(v.id)
                : true,
            )
            .map((vehicule: any, index: any) => (
              <div key={vehicule.id} className="victims">
                <div className="victim-item">
                  {featureService.hasFeature(FEATURES.MIGRATION_CLAIM_INFO) &&
                  !isEmpty(
                    getVehicleFromSagisInfo(
                      get(vehicule, 'partKey'),
                      props.sagisOpposingParties,
                    ),
                  )
                    ? get(
                        getVehicleFromSagisInfo(
                          get(vehicule, 'partKey'),
                          props.sagisOpposingParties,
                        ),
                        'registrationNumber',
                      )
                    : `${vehicule.registrationNumber}`}
                </div>
                <>
                  {featureService.hasFeature(
                    FEATURES.REASONS_FOR_NON_COMPENSATION,
                  ) ? (
                    <JudgementForNonCompensation
                      judgementAddId={judgementAddId}
                      judgementAddDocExpertises={judgementAddDocExpertises}
                      dahirAmounts={props.dahirAmounts}
                      isAppeal={isAppeal}
                      setExpertiseFieldValue={setAccountingExpertiseFieldValue}
                      handleVehiculeChange={handleVehiculeChange}
                      vehicle={vehicule}
                      index={index}
                      expertises={expertises}
                      isInputChanged={isInputChanged}
                      expertiseType={EXPERTISES.ACCOUNTING_EXPERTISE}
                      judgementOnMeritsRequest={props.judgementOnMeritsRequest}
                      setJudgementOnMeritsRequest={
                        props.setJudgementOnMeritsRequest
                      }
                      listings={props.listings}
                      setExpertises={setExpertises}
                      setJudgement={props.setJudgement}
                      judgement={props.judgement}
                      expertiseJudgementAddDocumentId={
                        vehicule?.expertiseJudgementAddDocumentId
                      }
                      judgementForNonCompensation={
                        JUDGEMENT_EXPERTISE_TYPES.CIVIL
                      }
                      modeType={MODE_TYPES.FORM}
                      withAccountingExpertise={hasAccountingExpertise(
                        recentAccountingVehiculesExpertises,
                        vehicule?.id,
                      )}
                      isDisabled={get(props.isDisabled, judgementAddId, false)}
                      allJudgementDocuments={props.allJudgementDocuments}
                      sharedResponsability={props.sharedResponsability}
                    />
                  ) : (
                    <VehicleExpertise
                      dahirAmounts={props.dahirAmounts}
                      isAppeal={isAppeal}
                      setExpertiseFieldValue={setAccountingExpertiseFieldValue}
                      handleVehiculeChange={handleVehiculeChange}
                      vehicle={vehicule}
                      index={index}
                      expertiseType={EXPERTISES.ACCOUNTING_EXPERTISE}
                      expertises={expertises}
                      isInputChanged={isInputChanged}
                      sagisOpposingParties={props.sagisOpposingParties}
                      isDisabled={get(props.isDisabled, judgementAddId, false)}
                    />
                  )}
                </>
              </div>
            ))}
        </div>
      )}
      <div>
        <section className="submit-form">
          <SecondaryButton
            data-testid="cancel"
            title={translate(messages.cancelButton, props)}
            onClick={handleCancel}
          />
          <PrimaryButton
            data-testid="save"
            disabled={props.isDisabled[judgementAddId]}
            background={BUTTON_COLORS.PRIMARY_BLUE}
            border={BUTTON_COLORS.PRIMARY_BLUE}
            titlecolor={BUTTON_COLORS.BLUE_BUTTON_TITLE}
            title={translate(messages.saveButton, props)}
            onClick={handleValidate}
          />
        </section>
      </div>
    </JudgementCivilContainer>
  );
};

export default injectIntl(CivilJudgement);
